<template>
  <modal
    name="flight-ticket-review"
    :width="'94%'"
    :height="'96%'"
    :max-width="1680"
    :adaptive="true"
    :click-to-close="false"
    :reset="true"
    class="flight-review-modal-container"
    @closed="reset"
  >
    <div class="flight-review-modal">
      <app-modal-header
        design="new"
        eyebrow="Flight ticket review"
        title-id="flight-ticket-review-title"
        :title="client ? client.full_name : 'Loading client…'"
        @close="close"
      />
      <div v-if="client && activeTicket" class="flight-review-modal__toolbar">
        <app-tabs
          design="new"
          :tabs="modalTabs"
          :value="modalMode"
          @input="changeModalMode"
        />
        <div class="flight-review-modal__header-actions">
          <router-link
            :to="{ name: 'clients-detail', params: { id: client.id } }"
            target="_blank"
            rel="noopener"
            class="flight-review-modal__client-link"
            :aria-label="`Open ${client.full_name} in a new tab`"
            title="Open client in a new tab"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M14 4h6v6M20 4l-9 9" />
              <path d="M18 13v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h5" />
            </svg>
            <span>Open client</span>
          </router-link>
        </div>
      </div>

      <app-spinner v-if="loading" design="new" />
      <div v-else-if="fatalError" class="flight-review-modal__fatal">
        <strong>Unable to load this flight ticket</strong>
        <p>{{ fatalError }}</p>
        <app-button design="new" size="small" @click.native="load">Try again</app-button>
      </div>

      <template v-else-if="client">
        <nav v-if="ticketItems.length > 1" class="flight-review-modal__file-switcher">
          <button
            v-for="ticket in ticketItems"
            :key="ticket.key"
            type="button"
            :class="{ 'flight-review-modal__file--active': ticket.key === activeFileKey }"
            @click="selectTicket(ticket)"
          >
            <app-status-badge
              design="new"
              :label="ticket.directions.length > 1 ? 'Shared arrival + departure' : directionNames(ticket.directions)"
              :tone="ticket.directions.length > 1 ? 'blue' : directionTone(ticket.directions[0])"
              compact
            />
            <strong :title="ticket.file.file_name">{{ ticket.file.file_name }}</strong>
          </button>
        </nav>

        <div v-if="actionError" class="flight-review-modal__error">{{ actionError }}</div>

        <div v-if="activeTicket" class="flight-review-modal__body">
          <main v-show="modalMode === 'review'" class="flight-review-modal__workspace">
            <section v-if="workspaceState !== 'review'" class="flight-recognition-state">
              <div class="flight-recognition-state__icon">AI</div>
              <h3>{{ recognitionHeading }}</h3>
              <p>{{ recognitionText }}</p>
              <template v-if="canEdit">
              <app-button
                design="new"
                v-if="workspaceState === 'ready'"
                :disabled="working"
                @click.native="startRecognition"
              >
                {{ working ? 'Analyzing ticket…' : recognitionButtonLabel }}
              </app-button>
              <app-button
                design="new"
                v-else-if="workspaceState === 'failed'"
                theme="info"
                :disabled="working"
                @click.native="reRecognize"
              >
                {{ working ? 'Analyzing ticket…' : 'Re-run AI recognition' }}
              </app-button>
              <app-button v-else design="new" disabled>
                Analyzing ticket…
              </app-button>
              </template>
            </section>

            <template v-else>
              <div
                v-if="reviewDetailsExpanded && (analysisWarnings.length || openIssues.length)"
                class="flight-review-alerts"
                :class="{ 'flight-review-alerts--single': !analysisWarnings.length || !openIssues.length }"
              >
                <section v-if="analysisWarnings.length" class="flight-recognition-warnings">
                  <strong>Recognition warnings</strong>
                  <ul>
                    <li v-for="warning in analysisWarnings" :key="warning">{{ warning }}</li>
                  </ul>
                </section>

                <section v-if="openIssues.length" class="flight-manual-review">
                  <header>
                    <div>
                      <span>Manual review required</span>
                      <strong>{{ openIssues.length }} open {{ openIssues.length === 1 ? 'issue' : 'issues' }}</strong>
                    </div>
                  </header>
                  <article v-for="issue in openIssues" :key="issue.id">
                    <div class="flight-manual-review__content">
                      <span :style="issueCategoryStyle(issue.category)">
                        {{ issueCategoryName(issue.category) }}
                      </span>
                      <p>{{ issue.description }}</p>
                    </div>
                    <div v-if="canEdit && resolutionIssueId !== issue.id" class="flight-manual-review__buttons">
                      <button type="button" :disabled="working" @click="openResolution(issue, 'resolved')">
                        Resolve
                      </button>
                      <button type="button" :disabled="working" @click="openResolution(issue, 'dismissed')">
                        Dismiss
                      </button>
                    </div>
                    <div v-else-if="canEdit" class="flight-manual-review__resolution">
                      <app-textarea
                        v-model="resolutionNote"
                        design="new"
                        rows="2"
                        placeholder="Resolution note"
                      />
                      <div>
                        <button type="button" @click="closeResolution">Cancel</button>
                        <button
                          type="button"
                          :disabled="working || !resolutionNote"
                          @click="resolveIssue(issue)"
                        >
                          Confirm {{ resolutionState === 'resolved' ? 'resolve' : 'dismiss' }}
                        </button>
                      </div>
                    </div>
                  </article>
                </section>
              </div>

              <section class="flight-review-tools">
                <div>
                  <span>Review summary</span>
                  <strong>{{ reviewSummaryLabel }}</strong>
                  <button
                    v-if="analysisWarnings.length || openIssues.length"
                    type="button"
                    class="flight-review-tools__details"
                    @click="reviewDetailsExpanded = !reviewDetailsExpanded"
                  >
                    {{ reviewDetailsExpanded ? 'Hide details' : 'Show details' }}
                  </button>
                </div>
                <div>
                  <span class="flight-review-tools__analysis">AI · {{ providerSummary }}</span>
                  <details v-if="canEdit" class="flight-review-tools__menu">
                    <summary aria-label="Open AI actions">•••</summary>
                    <div>
                      <button type="button" :disabled="working" @click="reRecognize">
                        Re-run recognition
                      </button>
                      <button type="button" :disabled="working || issueFormVisible" @click="openIssueForm">
                        Report an issue
                      </button>
                    </div>
                  </details>
                </div>
              </section>

              <form
                v-if="issueFormVisible"
                class="flight-issue-form"
                @submit.prevent="createIssue"
              >
                <header>
                  <div>
                    <span>Report an issue</span>
                    <strong>What should be checked manually?</strong>
                  </div>
                  <button type="button" aria-label="Cancel" @click="closeIssueForm">×</button>
                </header>
                <div class="flight-issue-form__fields">
                  <app-form-group design="new" label="Issue type" required>
                    <app-select
                      v-model="issueForm.category"
                      design="new"
                      :options="metadata.categories"
                      :reduce="item => item.code"
                      :clearable="false"
                      placeholder="Issue type"
                    />
                  </app-form-group>
                  <app-form-group design="new" label="Direction">
                    <app-select
                      v-model="issueForm.direction"
                      design="new"
                      :options="issueDirectionOptions"
                      :reduce="item => item.code"
                      :clearable="false"
                      placeholder="Direction"
                    />
                  </app-form-group>
                  <app-form-group design="new" label="Description" required class="flight-issue-form__description">
                    <app-textarea
                      v-model="issueForm.description"
                      design="new"
                      rows="3"
                      placeholder="Describe what is incorrect or should be checked"
                    />
                  </app-form-group>
                </div>
                <footer>
                  <app-button design="new" size="small" theme="dark" @click.native="closeIssueForm">Cancel</app-button>
                  <app-button
                    design="new"
                    type="submit"
                    size="small"
                    :disabled="working || !issueForm.category || !issueForm.description"
                  >
                    {{ working ? 'Creating…' : 'Create issue' }}
                  </app-button>
                </footer>
              </form>

              <section class="flight-direction-workspace">
                <header class="flight-direction-workspace__header">
                  <div
                    class="flight-direction-workspace__tabs"
                    role="tablist"
                    aria-label="Flight direction"
                  >
                    <button
                      v-for="direction in reviewDirections"
                      :key="`tab-${direction}`"
                      type="button"
                      role="tab"
                      :aria-selected="activeReviewDirection === direction ? 'true' : 'false'"
                      :class="[
                        `flight-direction-workspace__tab--${direction}`,
                        { 'flight-direction-workspace__tab--active': activeReviewDirection === direction },
                      ]"
                      @click="activeReviewDirection = direction"
                    >
                      <strong>{{ capitalize(direction) }}</strong>
                      <span>{{ directionStateLabel(direction) }}</span>
                    </button>
                  </div>
                </header>
                <div class="flight-direction-editors">
                  <direction-editor
                    v-for="direction in reviewDirections"
                    v-show="activeReviewDirection === direction"
                    :key="`${activeDocument.id}-${direction}`"
                    :direction="direction"
                    :segments="analysisSegments"
                    :selection="selectionFor(direction)"
                    :comparison="comparisonFor(direction)"
                    :airports="airports"
                    :issues="issuesFor(direction)"
                    :saving="working"
                    :readonly="!canEdit"
                    @save="saveSelection"
                    @dirty-change="setDirectionDirty"
                    @preview="openPreview"
                  />
                </div>
              </section>

              <section
                v-if="canEdit"
                class="flight-apply-block"
                :class="{ 'flight-apply-block--ready': canApplySelections }"
              >
                <div>
                  <small>Apply to client</small>
                  <strong>{{ applyHeading }}</strong>
                  <span v-if="canApplySelections && stagedChanges.length" class="flight-apply-block__summary">
                    {{ stagedChanges[0].summary }}
                    <template v-if="stagedChanges.length > 1">
                      · +{{ stagedChanges.length - 1 }} more
                    </template>
                  </span>
                  <span v-else-if="canApplySelections">The saved review drafts match current client data.</span>
                  <span v-else-if="unsavedEditDirections.length">
                    Save the review draft for {{ directionNames(unsavedEditDirections) }} first.
                  </span>
                  <span v-else-if="!canApplySelections">
                    Save a review draft for every available direction before applying.
                  </span>
                  <div v-if="applyDiffExpanded && stagedChanges.length" class="flight-apply-block__diff">
                    <span v-for="change in stagedChanges" :key="change.key">{{ change.summary }}</span>
                  </div>
                </div>
                <div class="flight-apply-block__actions">
                  <button
                    v-if="stagedChanges.length"
                    type="button"
                    @click="applyDiffExpanded = !applyDiffExpanded"
                  >
                    {{ applyDiffExpanded ? 'Hide diff' : 'Review diff' }}
                  </button>
                  <button
                    type="button"
                    class="flight-apply-block__primary"
                    :disabled="working || !canApplySelections"
                    @click="applySelections"
                  >
                    {{ working ? 'Applying…' : applyButtonLabel }}
                  </button>
                </div>
              </section>
            </template>
          </main>

          <flight-ticket-preview-panel
            v-show="modalMode === 'preview'"
            :file="activeTicket.file"
            :segments="analysisSegments"
            :warnings="analysisWarnings"
            :selected-segment-id="selectedPreviewSegmentId"
            :fallback-segment="previewSegment"
            :provider-summary="providerSummary"
            :status-name="statusName"
            :status-color="statusDisplay && statusDisplay.color"
            @select-segment="selectedPreviewSegmentId = $event"
          />
        </div>
      </template>
    </div>
  </modal>
</template>

<script>
import {
  getSupportFlightTicketReconciliationDetail,
  getLocations,
  postSupportFlightTicketApplication,
  postSupportFlightTicketIssue,
  postSupportFlightTicketIssueResolution,
  postSupportFlightTicketRecognition,
  postSupportFlightTicketReRecognition,
  postSupportFlightTicketSelection,
} from '@/http'
import DirectionEditor from './DirectionEditor'
import FlightTicketPreviewPanel from './FlightTicketPreviewPanel'
import { getFlightTicketIssueMetadata } from './metadata'
import { techSupportKey } from '@/utils/techSupportAccess'

const POLL_INTERVAL = 2000
const MAX_POLL_ATTEMPTS = 90

export default {
  name: 'FlightTicketReviewModal',
  components: { DirectionEditor, FlightTicketPreviewPanel },
  data() {
    return {
      clientId: null,
      client: null,
      activeFileKey: null,
      preferredDocumentId: null,
      loading: false,
      working: false,
      fatalError: null,
      actionError: null,
      airports: [],
      metadata: getFlightTicketIssueMetadata(),
      modalTabs: [
        { id: 'review', value: 'review', label: 'Review data' },
        { id: 'preview', value: 'preview', label: 'Preview ticket' },
      ],
      issueFormVisible: false,
      issueForm: {
        category: null,
        direction: '',
        description: null,
      },
      resolutionIssueId: null,
      resolutionState: null,
      resolutionNote: null,
      pollTimer: null,
      pollAttempts: 0,
      dirtyDirections: {},
      activeReviewDirection: null,
      modalMode: 'review',
      selectedPreviewSegmentId: null,
      reviewDetailsExpanded: false,
      applyDiffExpanded: false,
    }
  },
  computed: {
    canEdit() { return this.$store.getters['permissions/can'](techSupportKey('flight_tickets', 'edit')) },
    availableDirections() {
      if (!this.client) return []
      return ['arrival', 'departure'].filter(direction => {
        const ticket = this.client[`${direction}_ticket`]
        const state = this.client[`${direction}_ticket_state`]
        return Boolean(ticket || state?.is_uploaded)
      })
    },
    ticketItems() {
      if (!this.client) return []
      const items = []
      const byKey = {}
      const addTicket = (file, directions = [], document = null) => {
        if (!file) return
        const key = this.logicalFileKey(file)
        if (!byKey[key]) {
          byKey[key] = { key, file, directions: [], documents: [] }
          items.push(byKey[key])
        }
        directions.forEach(direction => {
          if (!byKey[key].directions.includes(direction)) byKey[key].directions.push(direction)
        })
        if (document && !byKey[key].documents.some(item => item.id === document.id)) {
          byKey[key].documents.push(document)
        }
      }

      ;(this.client.documents || []).forEach(document => {
        addTicket(document.file, this.sourceDirections(document.source), document)
      })
      ;['arrival', 'departure'].forEach(direction => {
        const ticket = this.client[`${direction}_ticket`]
        if (ticket) addTicket(ticket, [direction])
      })

      if (items.length === 1) items[0].directions = [...this.availableDirections]
      items.forEach(item => {
        if (!item.directions.length) item.directions = [...this.availableDirections]
      })
      return items
    },
    activeTicket() {
      return this.ticketItems.find(ticket => ticket.key === this.activeFileKey)
        || this.ticketItems[0]
        || null
    },
    activeDocument() {
      if (!this.activeTicket) return null
      return this.activeTicket.documents.find(document => document.id === this.preferredDocumentId)
        || this.activeTicket.documents.find(document => document.current_analysis)
        || this.activeTicket.documents[0]
        || null
    },
    analysis() {
      return this.activeDocument?.current_analysis || null
    },
    analysisSegments() {
      return this.analysis?.segments || []
    },
    analysisWarnings() {
      return this.analysis?.warnings || []
    },
    isProcessing() {
      return this.statusCode === 'processing' || this.analysis?.status === 'processing'
    },
    isFailed() {
      return this.statusCode === 'failed' || this.analysis?.status === 'failed'
    },
    workspaceState() {
      if (this.isProcessing) return 'processing'
      if (this.isFailed) return 'failed'
      if (this.analysis?.status === 'succeeded') return 'review'
      return 'ready'
    },
    reviewDirections() {
      if (!this.activeTicket) return []
      if (
        this.activeTicket.directions.length > 1
        || this.activeDocument?.source === 'both'
        || this.activeDocument?.source === 'manual'
      ) {
        return [...this.availableDirections]
      }
      return [...this.activeTicket.directions]
    },
    selectedDirections() {
      return this.reviewDirections.filter(direction => (
        this.selectionFor(direction)?.source === 'user'
        && !this.directionDirtyFor(direction)
      ))
    },
    unsavedEditDirections() {
      return this.reviewDirections.filter(direction => (
        this.selectionFor(direction)?.source !== 'user'
        || this.directionDirtyFor(direction)
      ))
    },
    canApplySelections() {
      return Boolean(
        this.reviewDirections.length
        && this.selectedDirections.length === this.reviewDirections.length,
      )
    },
    stagedChanges() {
      const labels = {
        flight_number: 'Flight number',
        date: 'Date',
        time: 'Time',
        airport_iata: 'Airport',
        airline_name: 'Airline',
        flight_domestic: 'Domestic flight',
      }
      return this.reviewDirections.reduce((changes, direction) => {
        const comparison = this.comparisonFor(direction)
        if (!comparison) return changes
        const fields = comparison.differences || []
        fields.forEach(field => {
          const directionLabel = this.capitalize(direction)
          const fieldLabel = labels[field] || this.humanize(field)
          const currentValue = this.stagedValue(field, comparison.current?.[field])
          const reviewedValue = this.stagedValue(field, comparison.extracted?.[field])
          changes.push({
            key: [direction, field].join('-'),
            direction,
            field,
            summary: `${directionLabel} · ${fieldLabel}: ${currentValue} → ${reviewedValue}`,
          })
        })
        return changes
      }, [])
    },
    applyHeading() {
      if (!this.canApplySelections) return 'Review drafts are not ready yet'
      if (!this.stagedChanges.length) return 'Saved drafts contain no client changes'
      return `${this.stagedChanges.length} staged ${this.stagedChanges.length === 1 ? 'change is' : 'changes are'} ready for the client`
    },
    applyButtonLabel() {
      if (!this.stagedChanges.length) return 'Apply to client'
      return `Apply ${this.stagedChanges.length} ${this.stagedChanges.length === 1 ? 'change' : 'changes'} to client`
    },
    reviewSummaryLabel() {
      const parts = []
      if (this.openIssues.length) {
        parts.push(`${this.openIssues.length} review ${this.openIssues.length === 1 ? 'issue' : 'issues'}`)
      }
      if (this.analysisWarnings.length) {
        parts.push(`${this.analysisWarnings.length} ${this.analysisWarnings.length === 1 ? 'warning' : 'warnings'}`)
      }
      return parts.length ? parts.join(' · ') : 'No recognition issues'
    },
    previewSegment() {
      return this.analysisSegments.find(segment => segment.id === this.selectedPreviewSegmentId)
        || this.aiCandidateFor(this.activeReviewDirection)
        || this.analysisSegments[0]
        || null
    },
    openIssues() {
      return (this.activeDocument?.issues || []).filter(issue => issue.state === 'open')
    },
    issueDirectionOptions() {
      const wholeDocument = { code: '', name: 'Whole document' }
      const directions = this.reviewDirections.map(direction => (
        this.metadata.directions.find(item => item.code === direction)
        || { code: direction, name: this.capitalize(direction) }
      ))
      return [wholeDocument, ...directions]
    },
    recognitionHeading() {
      if (this.workspaceState === 'processing') return 'AI is working'
      if (this.workspaceState === 'failed') return 'Recognition failed'
      return 'Ready for recognition'
    },
    recognitionText() {
      if (this.workspaceState === 'processing') {
        return 'The ticket is being processed in the background. You can close this modal and return from the In review queue.'
      }
      if (this.workspaceState === 'failed') {
        return this.analysis?.error_message
          || this.activeDocument?.error_message
          || 'AI could not recognize this ticket. Review the error and run recognition again.'
      }
      return 'AI will extract all flight segments. Nothing is written to the client profile until a manager reviews and applies the selection.'
    },
    recognitionButtonLabel() {
      const directions = this.activeTicket?.directions || []
      const label = directions.length > 1 ? 'arrival & departure' : directions[0]
      return `Analyze ${label || 'ticket'}`
    },
    providerSummary() {
      if (!this.analysis) return 'Not set'
      const provider = this.analysis.provider === 'openai' ? 'OpenAI' : this.analysis.provider
      return [provider, this.analysis.provider_model].filter(Boolean).join(' · ') || 'Not set'
    },
    statusCode() {
      return typeof this.activeDocument?.status === 'object' ? this.activeDocument.status.code : this.activeDocument?.status
    },
    statusDisplay() {
      if (typeof this.activeDocument?.status === 'object') return this.activeDocument.status
      const candidates = [
        this.client?.reconciliation_state,
        this.client?.arrival_ticket_state?.status,
        this.client?.departure_ticket_state?.status,
      ]
      return candidates.find(item => item?.code === this.statusCode) || null
    },
    statusName() {
      return this.statusDisplay?.name || this.humanize(this.statusCode)
    },
  },
  watch: {
    reviewDirections: {
      immediate: true,
      handler(directions) {
        if (!directions.includes(this.activeReviewDirection)) {
          this.activeReviewDirection = directions[0] || null
        }
      },
    },
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    changeModalMode(mode) {
      if (mode === 'preview') {
        this.openPreview()
      } else {
        this.modalMode = mode
      }
    },
    open(clientId, options = {}) {
      this.clientId = clientId
      this.preferredDocumentId = options.documentId || null
      this.$modal.show('flight-ticket-review')
      return Promise.all([
        this.load(),
        this.loadAirports(),
      ]).then(() => {
        if (this.isProcessing) this.startPolling()
      })
    },
    close() {
      this.$modal.hide('flight-ticket-review')
    },
    reset() {
      this.stopPolling()
      this.clientId = null
      this.client = null
      this.activeFileKey = null
      this.preferredDocumentId = null
      this.loading = false
      this.working = false
      this.fatalError = null
      this.actionError = null
      this.dirtyDirections = {}
      this.activeReviewDirection = null
      this.modalMode = 'review'
      this.selectedPreviewSegmentId = null
      this.reviewDetailsExpanded = false
      this.applyDiffExpanded = false
      this.closeIssueForm()
      this.closeResolution()
    },
    load(options = {}) {
      if (!this.clientId) return Promise.resolve()
      const requestedClientId = this.clientId
      if (!options.silent) this.loading = true
      this.fatalError = null
      return getSupportFlightTicketReconciliationDetail(this.clientId)
        .then(response => {
          if (requestedClientId !== this.clientId) return
          this.client = response.data
          const preferredDocument = (this.client.documents || []).find(
            document => document.id === this.preferredDocumentId,
          )
          const activeStillExists = this.ticketItems.some(ticket => ticket.key === this.activeFileKey)
          if (preferredDocument) {
            this.activeFileKey = this.logicalFileKey(preferredDocument.file)
          } else if (!activeStillExists) {
            this.activeFileKey = this.ticketItems[0]?.key || null
          }
        })
        .catch(error => {
          if (!options.silent) this.fatalError = this.errorMessage(error)
          if (options.rethrow) throw error
        })
        .finally(() => {
          this.loading = false
        })
    },
    loadAirports() {
      if (this.airports.length) return Promise.resolve()
      return getLocations({ page_size: 1000 }).then(response => {
        const items = response.data.results || response.data
        this.airports = items.filter(location => location.short_name)
      }).catch(() => {
        this.airports = []
      })
    },
    selectTicket(ticket) {
      this.activeFileKey = ticket.key
      this.preferredDocumentId = null
      this.actionError = null
      this.selectedPreviewSegmentId = null
      this.reviewDetailsExpanded = false
      this.applyDiffExpanded = false
      this.closeIssueForm()
      this.closeResolution()
      if (this.isProcessing) this.startPolling()
      else this.stopPolling()
    },
    logicalFileKey(file) {
      return (file?.file_name || file?.file || file?.url || String(file?.id || ''))
        .trim()
        .toLowerCase()
    },
    sourceDirections(source) {
      if (source === 'both') return ['arrival', 'departure']
      if (source === 'arrival' || source === 'departure') return [source]
      return []
    },
    startRecognition() {
      if (!this.canEdit) return Promise.resolve()
      if (this.activeDocument?.current_analysis) return this.reRecognize()
      return this.recognize()
    },
    recognize() {
      if (!this.canEdit) return Promise.resolve()
      const directions = this.activeTicket.directions
      const direction = directions[0] || this.availableDirections[0]
      const source = directions.length > 1 ? 'both' : direction
      this.prepareAction()
      return postSupportFlightTicketRecognition(this.clientId, { direction, source })
        .then(response => this.handleRecognitionResponse(response))
        .catch(error => this.handleActionError(error))
    },
    reRecognize() {
      if (!this.canEdit) return Promise.resolve()
      if (!this.activeDocument) return this.recognize()
      this.prepareAction()
      this.closeIssueForm()
      return postSupportFlightTicketReRecognition(this.clientId, {
        document_id: this.activeDocument.id,
      }).then(response => this.handleRecognitionResponse(response))
        .catch(error => this.handleActionError(error))
    },
    handleRecognitionResponse(response) {
      this.preferredDocumentId = response.data.id
      if (response.status === 202 || response.data.status === 'processing') {
        this.startPolling()
        return
      }
      return this.load({ silent: true, rethrow: true }).then(() => {
        this.working = false
        this.notifySuccess('Ticket recognition completed')
        this.$emit('recognized')
      })
    },
    startPolling() {
      this.stopPolling()
      this.working = true
      this.pollAttempts = 0
      this.pollRecognition()
    },
    pollRecognition() {
      this.load({ silent: true, rethrow: true }).then(() => {
        if (!this.isProcessing) {
          this.stopPolling()
          this.working = false
          this.$emit('changed')
          if (!this.isFailed) this.notifySuccess('Ticket recognition completed')
          return
        }
        this.pollAttempts += 1
        if (this.pollAttempts >= MAX_POLL_ATTEMPTS) {
          this.stopPolling()
          this.working = false
          this.actionError = 'Recognition is still running. Close the modal and return from the In review queue later.'
          return
        }
        this.pollTimer = window.setTimeout(this.pollRecognition, POLL_INTERVAL)
      }).catch(error => this.handleActionError(error))
    },
    stopPolling() {
      window.clearTimeout(this.pollTimer)
      this.pollTimer = null
    },
    prepareAction() {
      this.stopPolling()
      this.working = true
      this.actionError = null
    },
    handleActionError(error) {
      this.stopPolling()
      this.working = false
      this.actionError = this.errorMessage(error)
    },
    selectionFor(direction) {
      return (this.activeDocument?.selections || []).find(
        selection => selection.direction === direction,
      ) || null
    },
    aiCandidateFor(direction) {
      const key = direction === 'arrival' ? 'is_arrival_candidate' : 'is_departure_candidate'
      return this.analysisSegments.find(segment => segment[key]) || null
    },
    directionStateKey(direction) {
      return `${this.activeDocument?.id || 'document'}:${direction}`
    },
    directionDirtyFor(direction) {
      return Boolean(this.dirtyDirections[this.directionStateKey(direction)])
    },
    setDirectionDirty({ direction, dirty }) {
      this.$set(this.dirtyDirections, this.directionStateKey(direction), dirty)
    },
    comparisonFor(direction) {
      return this.activeDocument?.comparison?.[direction] || null
    },
    issuesFor(direction) {
      return this.openIssues.filter(issue => {
        const issueDirection = issue.direction
          || issue.expected_data?.direction
          || issue.observed_data?.direction
        return !issueDirection || issueDirection === direction
      })
    },
    saveSelection(payload) {
      if (!this.canEdit) return Promise.resolve()
      this.prepareAction()
      return postSupportFlightTicketSelection(this.clientId, {
        document_id: this.activeDocument.id,
        ...payload,
      }).then(() => this.load({ silent: true, rethrow: true }))
        .then(() => {
          this.setDirectionDirty({ direction: payload.direction, dirty: false })
          this.working = false
          this.notifySuccess(`${this.capitalize(payload.direction)} review draft saved`)
          this.$emit('changed')
        })
        .catch(error => this.handleActionError(error))
    },
    applySelections() {
      if (!this.canEdit || !this.canApplySelections) return Promise.resolve()
      this.prepareAction()
      return postSupportFlightTicketApplication(this.clientId, {
        document_id: this.activeDocument.id,
        directions: this.selectedDirections,
      })
        .then(() => this.load({ silent: true, rethrow: true }))
        .then(() => {
          this.working = false
          this.notifySuccess('Selected flight data applied to the client')
          this.$emit('changed')
        })
        .catch(error => this.handleActionError(error))
    },
    openIssueForm() {
      this.issueFormVisible = true
      this.issueForm = {
        category: this.metadata.categories[0]?.code || null,
        direction: '',
        description: null,
      }
    },
    closeIssueForm() {
      this.issueFormVisible = false
      this.issueForm = { category: null, direction: '', description: null }
    },
    createIssue() {
      if (!this.canEdit) return Promise.resolve()
      const selection = this.issueForm.direction ? this.selectionFor(this.issueForm.direction) : null
      const expectedData = this.issueForm.direction ? { direction: this.issueForm.direction } : {}
      const observedData = selection ? { selection_source: selection.source } : {}
      this.prepareAction()
      return postSupportFlightTicketIssue(this.clientId, {
        document_id: this.activeDocument.id,
        analysis_id: this.analysis.id,
        segment_id: selection?.segment?.id || null,
        category: this.issueForm.category,
        description: this.issueForm.description,
        expected_data: expectedData,
        observed_data: observedData,
      }).then(() => this.load({ silent: true, rethrow: true }))
        .then(() => {
          this.working = false
          this.closeIssueForm()
          this.notifySuccess('Issue created')
          this.$emit('changed')
        })
        .catch(error => this.handleActionError(error))
    },
    openResolution(issue, state) {
      this.resolutionIssueId = issue.id
      this.resolutionState = state
      this.resolutionNote = null
    },
    closeResolution() {
      this.resolutionIssueId = null
      this.resolutionState = null
      this.resolutionNote = null
    },
    resolveIssue(issue) {
      if (!this.canEdit) return Promise.resolve()
      this.prepareAction()
      return postSupportFlightTicketIssueResolution(issue.id, {
        state: this.resolutionState,
        resolution_note: this.resolutionNote,
      }).then(() => this.load({ silent: true, rethrow: true }))
        .then(() => {
          this.working = false
          this.closeResolution()
          this.notifySuccess('Issue updated')
          this.$emit('changed')
        })
        .catch(error => this.handleActionError(error))
    },
    issueCategory(category) {
      return this.metadata.categories.find(item => item.code === category) || null
    },
    issueCategoryName(category) {
      return this.issueCategory(category)?.name || this.humanize(category)
    },
    issueCategoryStyle(category) {
      const color = this.issueCategory(category)?.color
      return color ? { color } : {}
    },
    directionNames(directions) {
      return directions.map(direction => {
        return this.metadata.directions.find(item => item.code === direction)?.name
          || this.capitalize(direction)
      }).join(' & ')
    },
    directionTone(direction) {
      return direction === 'arrival' ? 'green' : 'blue'
    },
    directionStateLabel(direction) {
      if (this.directionDirtyFor(direction)) return 'Unsaved changes'
      if (this.selectionFor(direction)?.source === 'user') return 'Draft saved'
      return 'Needs review'
    },
    openPreview(segmentId = null) {
      if (segmentId) this.selectedPreviewSegmentId = segmentId
      this.modalMode = 'preview'
    },
    stagedValue(field, value) {
      if (value === null || value === undefined || value === '') return 'Not set'
      if (field === 'flight_domestic') return value ? 'Yes' : 'No'
      if (field === 'time') return String(value).slice(0, 5)
      return value
    },
    capitalize(value) {
      return value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : ''
    },
    humanize(value) {
      if (!value) return 'Not set'
      const words = String(value).replace(/_/g, ' ')
      return `${words.charAt(0).toUpperCase()}${words.slice(1)}`
    },
    notifySuccess(title) {
      this.$notify({ type: 'success', title })
    },
    errorMessage(error) {
      const data = error?.response?.data
      const detail = data?.detail
      if (typeof detail === 'string') return detail
      if (typeof detail?.detail === 'string') return detail.detail
      if (Array.isArray(detail) && typeof detail[0]?.detail === 'string') {
        return detail[0].detail
      }
      const message = data?.message
      if (typeof message === 'string') return message
      if (typeof message?.detail === 'string') return message.detail
      return 'The request could not be completed. Please try again.'
    },
  },
}
</script>

<style lang="sass" scoped>
.flight-review-modal
  display: flex
  min-height: 0
  height: 100%
  flex-direction: column
  overflow: hidden
  background: $ui-surface-soft
  color: $ui-text
  font-family: $ui-font-body
  font-size: $ui-font-size-body

  h2,
  h3,
  h4,
  strong
    font-family: $ui-font-heading

.flight-review-modal__toolbar
  display: flex
  align-items: flex-start
  justify-content: space-between
  gap: $ui-space-16
  padding: $ui-space-16 $ui-space-24 0
  background: $ui-surface-soft
  flex: 0 0 auto

  \:deep(.tabs-list)
    flex: 1
    margin-bottom: $ui-space-16

.flight-review-modal__header-actions
  display: flex
  align-items: center
  gap: $ui-space-8

  > a
    display: inline-flex
    min-height: $ui-control-height
    align-items: center
    justify-content: center
    gap: $ui-space-8
    padding: 0 $ui-space-12
    border: 1px solid $ui-line-strong
    border-radius: $ui-control-radius
    background: $ui-surface
    color: $ui-muted
    cursor: pointer
    font-weight: 600
    text-decoration: none

    &:hover,
    &:focus-visible
      border-color: $ui-blue
      background: $ui-blue-soft
      color: $ui-blue

    &:focus-visible
      outline: 3px solid rgba($ui-blue, .18)
      outline-offset: 2px

  svg
    width: 19px
    height: 19px
    fill: none
    stroke: currentColor
    stroke-linecap: round
    stroke-linejoin: round
    stroke-width: 1.8

.flight-review-modal__fatal,
.flight-recognition-state
  display: flex
  align-items: center
  justify-content: center
  flex-direction: column
  min-height: 400px
  padding: 35px
  border: 1px solid $ui-line
  border-radius: 10px
  background: $ui-surface
  text-align: center

  strong,
  h3
    margin: 0 0 8px

  p
    max-width: 520px
    margin: 0 0 20px
    color: $color-text-second
    font-size: 13px
    line-height: 1.55

.flight-review-modal__fatal
  margin: 22px

.flight-review-modal__file-switcher
  display: flex
  flex: 0 0 auto
  gap: 12px
  padding: 16px 24px 0
  overflow-x: auto

  button
    min-width: 220px
    max-width: 340px
    padding: 12px 14px
    border: 1px solid $ui-line
    border-radius: 12px
    background: $ui-surface
    text-align: left
    cursor: pointer

  strong
    display: block
    overflow: hidden
    margin-top: 7px
    color: $ui-ink
    font-size: 14px
    text-overflow: ellipsis
    white-space: nowrap

.flight-review-modal__file--active
  border-color: $ui-blue !important
  background: $ui-blue-soft !important
  box-shadow: 0 0 0 2px rgba(23, 105, 224, .1)

.flight-review-modal__error
  flex: 0 0 auto
  margin: 14px 20px 0
  padding: 12px 15px
  border: 1px solid $ui-red
  border-radius: 6px
  background: $ui-red-soft
  color: $ui-red
  font-size: 14px

.flight-review-modal__body
  display: block
  min-height: 0
  flex: 1 1 auto
  padding: 16px 24px 24px
  overflow: hidden

.flight-review-modal__workspace
  display: flex
  width: 100%
  max-width: 1440px
  min-height: 0
  flex-direction: column
  height: 100%
  gap: 12px
  margin: 0 auto
  padding-right: 4px
  overflow-y: auto
  scrollbar-gutter: stable

  > *
    flex: 0 0 auto

.flight-recognition-state__icon
  display: flex
  align-items: center
  justify-content: center
  width: 52px
  height: 52px
  margin-bottom: 14px
  border-radius: 14px
  background: $color-theme
  color: $ui-surface
  font-size: 16px
  font-weight: 700

.flight-analysis-card
  flex: 0 0 auto
  padding: 14px 15px
  border: 1px solid $ui-line
  border-radius: 12px
  background: $ui-surface
  box-shadow: 0 6px 20px rgba(28, 45, 74, .045)

  header,
  > div
    display: flex
    align-items: center
    justify-content: space-between
    gap: 12px

  header
    padding-bottom: 11px
    border-bottom: 1px solid $ui-line

  > div
    padding-top: 11px

  header div
    min-width: 0

  span
    display: block
    margin-bottom: 2px
    color: $color-text-second
    font-size: 11px
    font-weight: 600
    text-transform: uppercase

  strong
    font-size: 14px

.flight-direction-workspace
  overflow: hidden
  border: 1px solid $ui-line
  border-radius: 14px
  background: $ui-surface
  box-shadow: 0 8px 26px rgba(28, 45, 74, .055)

.flight-direction-workspace__header
  display: flex
  align-items: center
  justify-content: flex-start
  gap: 18px
  padding: 14px 20px
  border-bottom: 1px solid $ui-line
  background: $ui-surface-soft

.flight-direction-workspace__tabs
  display: flex
  flex: 0 0 auto
  align-items: stretch
  gap: 8px

  button
    min-width: 138px
    padding: 8px 11px
    border: 1px solid $ui-line-strong
    border-radius: 10px
    background: $ui-surface
    color: $ui-muted
    text-align: left
    cursor: pointer
    transition: border-color .15s ease, background .15s ease, box-shadow .15s ease

    strong,
    span
      display: block

    strong
      color: $ui-ink
      font-size: 14px

    span
      margin-top: 2px
      font-size: 11px

    &:focus-visible
      outline: 2px solid $ui-blue
      outline-offset: 2px

  .flight-direction-workspace__tab--active
    box-shadow: 0 5px 14px rgba(28, 45, 74, .16)

    strong,
    span
      color: $ui-surface

  .flight-direction-workspace__tab--arrival.flight-direction-workspace__tab--active
    border-color: $ui-green
    background: $ui-green

  .flight-direction-workspace__tab--departure.flight-direction-workspace__tab--active
    border-color: $ui-blue
    background: $ui-blue

.flight-recognition-warnings
  padding: 12px 14px
  border: 1px solid $ui-amber
  border-radius: 8px
  background: $ui-amber-soft
  color: $ui-amber
  font-size: 14px

  ul
    margin: 7px 0 0
    padding-left: 18px

  li + li
    margin-top: 4px

.flight-review-alerts
  display: grid
  grid-template-columns: repeat(2, minmax(0, 1fr))
  gap: 12px

  > section
    min-width: 0

.flight-review-alerts--single
  grid-template-columns: 1fr

.flight-review-tools
  display: flex
  align-items: center
  justify-content: space-between
  gap: 20px
  padding: 9px 12px
  border: 1px solid $ui-line
  border-radius: 8px
  background: $ui-surface

  strong,
  span
    display: block

  > div:first-child > span:first-child
    margin-bottom: 2px
    color: $ui-blue
    font-size: 10px
    font-weight: 600
    text-transform: uppercase

  strong
    display: inline
    margin-right: 8px
    font-size: 14px

  > div:last-child
    display: flex
    position: relative
    align-items: center
    flex-shrink: 0
    gap: 8px

.flight-review-tools__details
  padding: 0
  border: 0
  background: transparent
  color: $ui-blue
  font-size: 12px
  font-weight: 600
  cursor: pointer

.flight-review-tools__analysis
  color: $ui-muted
  font-size: 12px

.flight-review-tools__menu
  position: relative

  summary
    display: flex
    width: 32px
    height: 32px
    align-items: center
    justify-content: center
    border: 1px solid $ui-line
    border-radius: 7px
    background: $ui-surface
    color: $ui-muted
    font-weight: 700
    list-style: none
    cursor: pointer

    &::-webkit-details-marker
      display: none

  > div
    display: grid
    position: absolute
    top: calc(100% + 6px)
    right: 0
    z-index: 8
    min-width: 180px
    padding: 5px
    border: 1px solid $ui-line
    border-radius: 8px
    background: $ui-surface
    box-shadow: 0 10px 25px rgba(28, 45, 74, .15)

    button
      padding: 8px 9px
      border: 0
      border-radius: 6px
      background: transparent
      color: $ui-text
      font-size: 12px
      text-align: left
      cursor: pointer

      &:hover,
      &:focus-visible
        background: $ui-blue-soft
        color: $ui-blue

.flight-issue-form
  padding: 14px
  border: 1px solid $ui-red
  border-radius: 9px
  background: $ui-surface

  > header
    display: flex
    align-items: flex-start
    justify-content: space-between
    gap: 12px
    margin-bottom: 12px

    span
      display: block
      margin-bottom: 2px
      color: $color-error
      font-size: 12px
      font-weight: 600
      text-transform: uppercase

    strong
      font-size: 15px

    button
      border: 0
      background: transparent
      color: $color-text-second
      font-size: 20px
      cursor: pointer

  > footer
    display: flex
    justify-content: flex-end
    gap: 8px
    margin-top: 12px

.flight-issue-form__fields
  display: grid
  grid-template-columns: repeat(2, minmax(0, 1fr))
  gap: 10px

.flight-issue-form__description
  grid-column: 1 / -1

.flight-manual-review
  overflow: hidden
  border: 1px solid $ui-red
  border-radius: 9px
  background: $ui-surface

  > header
    padding: 12px 14px
    background: $ui-red-soft

    span
      display: block
      color: $color-error
      font-size: 12px
      font-weight: 600
      text-transform: uppercase

    strong
      font-size: 14px

  article
    display: flex
    align-items: center
    justify-content: space-between
    gap: 12px
    padding: 11px 14px
    border-top: 1px solid $ui-red-soft

.flight-manual-review__content
  span
    font-size: 12px
    font-weight: 600
    text-transform: uppercase

  p
    margin: 3px 0 0
    font-size: 14px

.flight-manual-review__buttons,
.flight-manual-review__resolution > div
  display: flex
  flex-shrink: 0
  justify-content: flex-end
  gap: 6px

  button
    padding: 6px 9px
    border: 1px solid $ui-red
    border-radius: 6px
    background: $ui-surface
    color: $ui-red
    font-size: 13px
    transition: border-color .2s, background-color .2s, color .2s
    cursor: pointer

.flight-manual-review__buttons button
  &:not(:disabled):hover,
  &:not(:disabled):focus-visible
    border-color: $ui-red
    background: $ui-red
    color: $ui-surface

.flight-manual-review__resolution
  width: 60%
  max-width: 420px

  > div
    margin-top: 6px

.flight-direction-editors
  display: block
  padding: 0

.flight-apply-block
  display: flex
  position: sticky
  bottom: 0
  z-index: 3
  align-items: center
  justify-content: space-between
  gap: 20px
  padding: 14px 18px
  border: 1px solid $ui-line-strong
  border-radius: 10px
  background: $ui-surface
  box-shadow: 0 -7px 20px rgba(28, 45, 74, .08)

  small,
  strong,
  span
    display: block

  small
    margin-bottom: 4px
    color: $ui-blue
    font-size: 12px
    font-weight: 600
    text-transform: uppercase

  strong
    margin-bottom: 3px
    color: $ui-ink
    font-size: 15px

  span
    color: $ui-muted
    font-size: 13px
    line-height: 1.4

  .flight-apply-block__summary
    margin-bottom: 4px
    color: $ui-ink
    font-weight: 600

.flight-apply-block--ready
  border-color: $ui-blue
  box-shadow: 0 -7px 22px rgba(23, 105, 224, .1)

.flight-apply-block__actions
  display: flex
  flex-shrink: 0
  align-items: center
  gap: 10px

  > button
    padding: 8px 10px
    border: 0
    background: transparent
    color: $ui-blue
    font-size: 13px
    font-weight: 600
    cursor: pointer

.flight-apply-block__primary
  min-height: 40px
  padding: 0 17px !important
  border: 1px solid $ui-blue !important
  border-radius: 8px
  background: $ui-blue !important
  color: $ui-surface !important
  font-weight: 700
  box-shadow: 0 4px 12px rgba(23, 105, 224, .2)

  &:not(:disabled):hover,
  &:not(:disabled):focus-visible
    background: $ui-blue-hover !important
    box-shadow: 0 6px 16px rgba(23, 105, 224, .25)

  &:focus-visible
    outline: 2px solid $ui-blue
    outline-offset: 2px

  &:disabled
    border-color: $ui-line-strong !important
    background: $ui-line !important
    color: $ui-muted !important
    box-shadow: none
    cursor: not-allowed

.flight-apply-block__diff
  display: grid
  gap: 3px
  margin-top: 8px
  padding-top: 8px
  border-top: 1px solid $ui-line

  span
    color: $ui-text
    font-size: 12px

@media (max-width: 1100px)
  .flight-review-modal__body
    overflow-y: auto

  .flight-review-modal__workspace
    height: auto
    overflow: visible

@media (max-width: 750px)
  .flight-issue-form__fields
    grid-template-columns: 1fr

  .flight-issue-form__description
    grid-column: auto

  .flight-review-tools,
  .flight-apply-block,
  .flight-manual-review article,
  .flight-direction-workspace__header
    align-items: stretch
    flex-direction: column

  .flight-review-modal__header-main
    align-items: flex-start
    flex-direction: column
    gap: 10px

  .flight-review-modal__mode-switch
    width: 100%

    button
      flex: 1

  .flight-review-alerts
    grid-template-columns: 1fr

  .flight-direction-workspace__tabs
    width: 100%

    button
      min-width: 0
      flex: 1

  .flight-review-tools > div:last-child
    justify-content: space-between

  .flight-apply-block__actions
    justify-content: space-between

  .flight-manual-review__resolution
    width: 100%
</style>

<style lang="scss">
.flight-review-modal-container .vm--modal {
  height: 96vh !important;
  max-height: 96vh !important;
  overflow: hidden !important;
}
</style>
